import React, { useEffect } from 'react';
import Layout from "../temp/layout/Layout";
import { Helmet } from 'react-helmet';
import AOS from 'aos';
import 'aos/dist/aos.css';
import { Link } from 'react-router-dom';
import { 
    FaFileAlt, FaClipboardCheck, FaCalendarCheck, FaLaptopCode,
    FaCreditCard, FaChalkboardTeacher, FaBookReader, FaUserTie,
    FaUsers, FaGraduationCap, FaBrain, FaTasks, FaVideo,
    FaCheckCircle, FaArrowRight, FaRocket, FaClock, FaCalendarAlt,
    FaBook, FaCertificate, FaChartLine, FaEnvelope, FaBell,
    FaFileInvoice, FaBus, FaBed, FaWarehouse, FaComments,
    FaAward, FaMoneyBillWave, FaCalculator, FaFilePdf, FaPrint
} from 'react-icons/fa';

const Features = () => {
    useEffect(() => {
        AOS.init({ 
            duration: 1000, 
            once: true,
            offset: 100
        });
    }, []);

    const features = [
        {
            icon: <FaFileAlt className="text-5xl" />,
            title: "Admission Form Management",
            description: "Streamlined digital admission forms with automated validation and instant submission. Reduce paperwork and processing time by 90%.",
            color: "from-blue-500 to-blue-600",
            benefits: ["Online form submission", "Automated validation", "Instant notifications", "Document upload"]
        },
        {
            icon: <FaClipboardCheck className="text-5xl" />,
            title: "Admission Form Review",
            description: "AI-powered review system that helps administrators quickly process and approve admission applications with intelligent recommendations.",
            color: "from-purple-500 to-purple-600",
            benefits: ["AI-powered review", "Quick processing", "Intelligent recommendations", "Automated approval workflow"]
        },
        {
            icon: <FaCalendarCheck className="text-5xl" />,
            title: "Entrance Exam Scheduling",
            description: "Automated exam scheduling with conflict detection, room allocation, and automated notifications to students and invigilators.",
            color: "from-green-500 to-green-600",
            benefits: ["Auto scheduling", "Conflict detection", "Room allocation", "Smart notifications"]
        },
        {
            icon: <FaLaptopCode className="text-5xl" />,
            title: "Online Entrance Exam",
            description: "Secure, proctored online examination system with real-time monitoring, anti-cheating measures, and instant result generation.",
            color: "from-red-500 to-red-600",
            benefits: ["Secure platform", "Real-time monitoring", "Anti-cheating", "Instant results"]
        },
        {
            icon: <FaCreditCard className="text-5xl" />,
            title: "Admission Fee Payment",
            description: "Multiple payment gateway integration with instant receipts, payment tracking, and automated reminders for pending payments.",
            color: "from-yellow-500 to-yellow-600",
            benefits: ["Multiple gateways", "Instant receipts", "Payment tracking", "Auto reminders"]
        },
        {
            icon: <FaClock className="text-5xl" />,
            title: "Attendance Management",
            description: "Comprehensive attendance tracking system with biometric integration, automated marking, SMS alerts, and detailed reports.",
            color: "from-indigo-500 to-indigo-600",
            benefits: ["Biometric integration", "Automated marking", "SMS alerts", "Detailed reports", "Parent notifications"]
        },
        {
            icon: <FaCalendarAlt className="text-5xl" />,
            title: "Class Scheduling",
            description: "Intelligent class scheduling system with automatic timetable generation, teacher availability, room booking, and conflict resolution.",
            color: "from-teal-500 to-teal-600",
            benefits: ["Auto timetable generation", "Teacher availability", "Room booking", "Conflict resolution", "Flexible scheduling"]
        },
        {
            icon: <FaChalkboardTeacher className="text-5xl" />,
            title: "Additional Classes",
            description: "Manage extra classes, remedial sessions, and special coaching with scheduling, attendance tracking, and progress monitoring.",
            color: "from-pink-500 to-pink-600",
            benefits: ["Extra class scheduling", "Remedial sessions", "Special coaching", "Progress monitoring"]
        },
        {
            icon: <FaFilePdf className="text-5xl" />,
            title: "Marksheet Generation",
            description: "Automated marksheet and report card generation with customizable templates, digital signatures, and instant PDF download.",
            color: "from-orange-500 to-orange-600",
            benefits: ["Auto generation", "Customizable templates", "Digital signatures", "PDF download", "Bulk printing"]
        },
        {
            icon: <FaCertificate className="text-5xl" />,
            title: "Certificate Generation",
            description: "Generate certificates, transcripts, and academic documents automatically with official templates and digital verification.",
            color: "from-cyan-500 to-cyan-600",
            benefits: ["Auto certificate generation", "Official templates", "Digital verification", "Bulk processing"]
        },
        {
            icon: <FaBookReader className="text-5xl" />,
            title: "Class Operations",
            description: "Manage daily class activities, assignments, and academic schedules with automated workflows and notifications.",
            color: "from-rose-500 to-rose-600",
            benefits: ["Daily activities", "Assignment management", "Automated workflows", "Class announcements"]
        },
        {
            icon: <FaUserTie className="text-5xl" />,
            title: "Teacher Management",
            description: "Comprehensive teacher profiles, performance tracking, workload management, and professional development tracking.",
            color: "from-violet-500 to-violet-600",
            benefits: ["Teacher profiles", "Performance tracking", "Workload management", "Professional development"]
        },
        {
            icon: <FaUsers className="text-5xl" />,
            title: "Student Management",
            description: "Complete student lifecycle management from admission to graduation with detailed academic records and performance analytics.",
            color: "from-amber-500 to-amber-600",
            benefits: ["Lifecycle management", "Academic records", "Performance analytics", "Progress tracking"]
        },
        {
            icon: <FaGraduationCap className="text-5xl" />,
            title: "Administration",
            description: "Centralized administrative control with role-based access, comprehensive reporting, and institutional data management.",
            color: "from-emerald-500 to-emerald-600",
            benefits: ["Centralized control", "Role-based access", "Comprehensive reports", "Data management"]
        },
        {
            icon: <FaBrain className="text-5xl" />,
            title: "AI Mentorship",
            description: "Revolutionary AI-powered mentorship system that provides personalized learning paths, study recommendations, and performance insights.",
            color: "from-purple-500 to-pink-600",
            benefits: ["Personalized learning", "Study recommendations", "Performance insights", "AI-powered analysis"],
            featured: true
        },
        {
            icon: <FaTasks className="text-5xl" />,
            title: "Assignment Management",
            description: "Digital assignment creation, submission, grading, and feedback system with plagiarism detection and automated grading.",
            color: "from-blue-500 to-indigo-600",
            benefits: ["Digital creation", "Online submission", "Plagiarism detection", "Automated grading"]
        },
        {
            icon: <FaClipboardCheck className="text-5xl" />,
            title: "Online Exam System",
            description: "Comprehensive online examination platform with question banks, auto-grading, detailed analytics, and secure exam delivery.",
            color: "from-green-500 to-emerald-600",
            benefits: ["Question banks", "Auto-grading", "Detailed analytics", "Secure delivery"]
        },
        {
            icon: <FaVideo className="text-5xl" />,
            title: "Online Classes",
            description: "Integrated virtual classroom with video conferencing, screen sharing, interactive whiteboard, and session recording capabilities.",
            color: "from-red-500 to-pink-600",
            benefits: ["Video conferencing", "Screen sharing", "Interactive whiteboard", "Session recording"]
        },
        {
            icon: <FaMoneyBillWave className="text-5xl" />,
            title: "Fee Management",
            description: "Complete fee management system with online payments, fee structure management, payment reminders, and financial reports.",
            color: "from-yellow-500 to-orange-600",
            benefits: ["Online payments", "Fee structure", "Payment reminders", "Financial reports", "Receipt generation"]
        },
        {
            icon: <FaBook className="text-5xl" />,
            title: "Library Management",
            description: "Digital library management with book cataloging, issue/return tracking, fine management, and member management.",
            color: "from-indigo-500 to-purple-600",
            benefits: ["Book cataloging", "Issue/return tracking", "Fine management", "Member management", "Search functionality"]
        },
        {
            icon: <FaBed className="text-5xl" />,
            title: "Hostel Management",
            description: "Complete hostel management system with room allocation, mess management, visitor tracking, and maintenance requests.",
            color: "from-teal-500 to-cyan-600",
            benefits: ["Room allocation", "Mess management", "Visitor tracking", "Maintenance requests", "Fee management"]
        },
        {
            icon: <FaBus className="text-5xl" />,
            title: "Transport Management",
            description: "School transport management with route planning, vehicle tracking, driver management, and parent notifications.",
            color: "from-blue-500 to-cyan-600",
            benefits: ["Route planning", "Vehicle tracking", "Driver management", "Parent notifications", "GPS integration"]
        },
        {
            icon: <FaWarehouse className="text-5xl" />,
            title: "Inventory Management",
            description: "School inventory and asset management with stock tracking, purchase orders, asset allocation, and depreciation tracking.",
            color: "from-gray-500 to-gray-700",
            benefits: ["Stock tracking", "Purchase orders", "Asset allocation", "Depreciation tracking", "Reports"]
        },
        {
            icon: <FaComments className="text-5xl" />,
            title: "Communication Portal",
            description: "Integrated communication platform for teachers, students, and parents with messaging, announcements, and notifications.",
            color: "from-green-500 to-teal-600",
            benefits: ["Messaging system", "Announcements", "Notifications", "Group chats", "File sharing"]
        },
        {
            icon: <FaUsers className="text-5xl" />,
            title: "Parent Portal",
            description: "Dedicated parent portal to track child's attendance, grades, assignments, fee payments, and communicate with teachers.",
            color: "from-pink-500 to-rose-600",
            benefits: ["Attendance tracking", "Grade viewing", "Assignment status", "Fee payments", "Teacher communication"]
        },
        {
            icon: <FaChartLine className="text-5xl" />,
            title: "Report Card Generation",
            description: "Automated report card generation with grade calculation, performance analysis, and customizable report formats.",
            color: "from-purple-500 to-indigo-600",
            benefits: ["Auto grade calculation", "Performance analysis", "Customizable formats", "Bulk generation", "PDF export"]
        },
        {
            icon: <FaEnvelope className="text-5xl" />,
            title: "SMS/Email Notifications",
            description: "Automated SMS and email notifications for attendance, fees, exams, events, and important announcements.",
            color: "from-blue-500 to-cyan-600",
            benefits: ["SMS notifications", "Email alerts", "Custom templates", "Bulk messaging", "Delivery tracking"]
        },
        {
            icon: <FaBell className="text-5xl" />,
            title: "Notice Board",
            description: "Digital notice board for announcements, circulars, events, and important information with category filtering.",
            color: "from-yellow-500 to-amber-600",
            benefits: ["Digital notices", "Category filtering", "Priority marking", "Expiry dates", "Attachment support"]
        },
        {
            icon: <FaFileInvoice className="text-5xl" />,
            title: "Document Management",
            description: "Centralized document management system with secure storage, version control, access permissions, and search functionality.",
            color: "from-gray-500 to-slate-600",
            benefits: ["Secure storage", "Version control", "Access permissions", "Search functionality", "Cloud backup"]
        },
        {
            icon: <FaClipboardCheck className="text-5xl" />,
            title: "Gradebook",
            description: "Comprehensive gradebook system with grade entry, calculation, weightage management, and performance tracking.",
            color: "from-green-500 to-emerald-600",
            benefits: ["Grade entry", "Auto calculation", "Weightage management", "Performance tracking", "Analytics"]
        },
        {
            icon: <FaCalendarAlt className="text-5xl" />,
            title: "Time Table Management",
            description: "Advanced timetable management with automatic generation, teacher-subject mapping, room allocation, and conflict detection.",
            color: "from-indigo-500 to-blue-600",
            benefits: ["Auto generation", "Teacher mapping", "Room allocation", "Conflict detection", "Flexible editing"]
        },
        {
            icon: <FaClipboardCheck className="text-5xl" />,
            title: "Exam Management",
            description: "Complete exam management system with exam scheduling, hall ticket generation, result processing, and analytics.",
            color: "from-red-500 to-orange-600",
            benefits: ["Exam scheduling", "Hall tickets", "Result processing", "Analytics", "Grade distribution"]
        },
        {
            icon: <FaChartLine className="text-5xl" />,
            title: "Result Management",
            description: "Automated result management with grade calculation, result publication, transcript generation, and performance analysis.",
            color: "from-purple-500 to-pink-600",
            benefits: ["Grade calculation", "Result publication", "Transcript generation", "Performance analysis", "Statistics"]
        },
        {
            icon: <FaAward className="text-5xl" />,
            title: "Scholarship Management",
            description: "Scholarship and financial aid management with application processing, eligibility checking, and disbursement tracking.",
            color: "from-yellow-500 to-amber-600",
            benefits: ["Application processing", "Eligibility checking", "Disbursement tracking", "Reports", "Notifications"]
        },
        {
            icon: <FaMoneyBillWave className="text-5xl" />,
            title: "Staff Payroll",
            description: "Complete staff payroll management with salary calculation, deductions, tax management, and payslip generation.",
            color: "from-green-500 to-teal-600",
            benefits: ["Salary calculation", "Deductions", "Tax management", "Payslip generation", "Financial reports"]
        },
        {
            icon: <FaCalculator className="text-5xl" />,
            title: "Budget Management",
            description: "Institutional budget planning and management with expense tracking, budget allocation, and financial forecasting.",
            color: "from-blue-500 to-indigo-600",
            benefits: ["Budget planning", "Expense tracking", "Budget allocation", "Financial forecasting", "Reports"]
        },
        {
            icon: <FaPrint className="text-5xl" />,
            title: "ID Card Generation",
            description: "Automated ID card generation for students and staff with QR codes, photos, and customizable templates.",
            color: "from-purple-500 to-violet-600",
            benefits: ["Auto generation", "QR codes", "Photo integration", "Customizable templates", "Bulk printing"]
        },
    ];

    return (
        <Layout>
            <Helmet>
                <title>Features - Educational Eternity | Complete Education ERP Features</title>
                <meta name="description" content="Explore all features of Educational Eternity - AI-powered Education ERP system. Admission management, online exams, fee payment, class management, AI mentorship, and more." />
                <meta name="keywords" content="Education ERP Features, School Management Features, Online Exam System, Admission Management, AI Learning System" />
            </Helmet>

            {/* Hero Section */}
            <section className="relative w-full h-[50vh] bg-gradient-to-br from-blue-600 via-indigo-700 to-purple-800 flex items-center justify-center overflow-hidden">
                <div className="absolute inset-0 opacity-20" style={{
                    backgroundImage: `url(assets/images/home1.jpg)`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center'
                }}></div>
                <div className="relative z-10 text-center px-4">
                    <h1 data-aos="fade-up" className="text-5xl md:text-7xl font-bold text-white mb-4">
                        Our Features
                    </h1>
                    <p data-aos="fade-up" data-aos-delay="100" className="text-xl md:text-2xl text-blue-100 max-w-3xl mx-auto">
                        Complete suite of features to transform your educational institution
                    </p>
                </div>
            </section>

            {/* Features Grid */}
            <section className="py-20 bg-gray-50">
                <div className="container mx-auto px-4">
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {features.map((feature, idx) => (
                            <div
                                key={idx}
                                data-aos="fade-up"
                                data-aos-delay={idx * 50}
                                className={`bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 border-2 ${
                                    feature.featured ? 'border-purple-500 ring-4 ring-purple-200' : 'border-gray-100'
                                }`}
                            >
                                {feature.featured && (
                                    <div className="absolute -top-3 right-4 bg-gradient-to-r from-purple-600 to-pink-600 text-white px-4 py-1 rounded-full text-xs font-bold">
                                        AI-Powered
                                    </div>
                                )}
                                <div className={`inline-flex p-4 rounded-xl bg-gradient-to-r ${feature.color} text-white mb-6`}>
                                    {feature.icon}
                                </div>
                                <h3 className="text-2xl font-bold text-gray-900 mb-4">{feature.title}</h3>
                                <p className="text-gray-600 leading-relaxed mb-6">{feature.description}</p>
                                <ul className="space-y-2">
                                    {feature.benefits.map((benefit, i) => (
                                        <li key={i} className="flex items-center text-gray-700">
                                            <FaCheckCircle className="text-green-500 mr-2 flex-shrink-0" />
                                            <span className="text-sm">{benefit}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="py-20 bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600">
                <div className="container mx-auto px-4 text-center" data-aos="fade-up">
                    <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
                        Ready to Experience These Features?
                    </h2>
                    <p className="text-xl text-blue-100 mb-8 max-w-3xl mx-auto">
                        Schedule a free demo and see how Educational Eternity can transform your institution.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-4 justify-center">
                        <Link to="/contact">
                            <button className="px-8 py-4 bg-white text-blue-600 rounded-full text-lg font-bold shadow-2xl hover:shadow-white/50 transform hover:scale-105 transition-all duration-300 flex items-center gap-2 mx-auto">
                                Schedule Demo <FaArrowRight />
                            </button>
                        </Link>
                        <Link to="/plans">
                            <button className="px-8 py-4 bg-transparent border-2 border-white text-white rounded-full text-lg font-bold hover:bg-white hover:text-blue-600 transform hover:scale-105 transition-all duration-300">
                                View Pricing
                            </button>
                        </Link>
                    </div>
                </div>
            </section>
        </Layout>
    );
};

export default Features;

